import React, { useRef, useMemo } from 'react';
import useTypingAnimation from '../hooks/useTypingAnimation';
import VariableProximity from './VariableProximity';
import LiquidEther from './LiquidEther';
import Lanyard from './Lanyard';

const Home = ({ containerRef }) => {
  const typingElementRef = useRef(null);
  
  const jobTitles = useMemo(() => [
    "Experience Architect",
    "Idea Sculptor", 
    "Immersive Storyteller",
  ], []);

  const typingOptions = useMemo(() => ({
    typingSpeed: 100,
    deletingSpeed: 50,
    pauseTime: 2000,
    startDelay: 1000
  }), []);

  useTypingAnimation(typingElementRef, jobTitles, typingOptions);

  const commonProps = {
    fromFontVariationSettings: "'wght' 400, 'opsz' 9",
    toFontVariationSettings: "'wght' 600, 'opsz' 30",
    containerRef: containerRef,
    radius: 100,
    falloff: "linear"
  };

  const heroSectionRef = useRef(null);
  const liquidBgRef = useRef(null);
  const frameworkDivRef = useRef(null);

  return (
    /* Shared wrapper — LiquidEther covers both hero and framework sections */
    <div style={{ position: 'relative', backgroundColor: 'black' }}>
      {/* LiquidEther Background Animation — positioned over full wrapper */}
      <div ref={liquidBgRef} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        opacity: 1,
        pointerEvents: 'none',
      }}>
        <LiquidEther
          colors={["#5227FF", "#FF9FFC", "#B19EEF"]}
          mouseForce={20}
          cursorSize={150}
          dt={0.014}
          BFECC={true}
          isViscous={true}
          viscous={30}
          iterationsViscous={32}
          iterationsPoisson={32}
          resolution={0.5}
          isBounce={false}
          autoDemo={true}
          autoSpeed={0.15}
          autoIntensity={2.2}
          takeoverDuration={0.25}
          autoResumeDelay={1000}
          autoRampDuration={0.6}
        />
      </div>

      <section className="home" ref={heroSectionRef} style={{ position: 'relative', zIndex: 1, backgroundColor: 'transparent' }}>
        <div className="home-img">
          <Lanyard
            position={[0, 0, 7]}
            gravity={[0, -40, 0]}
            fov={35}
            transparent={true}
          />
        </div>
        <div className="home-content">
          <h1>
            <VariableProximity
              label="Hi, It's Sukhada"
              fromFontVariationSettings="'wght' 400, 'opsz' 9"
              toFontVariationSettings="'wght' 900, 'opsz' 40"
              containerRef={containerRef}
              radius={100}
              falloff="linear"
            />
          </h1>
          <h3 className="typing-text">I'm an <span ref={typingElementRef} key="typing-animation"></span></h3>
          <p>
            <VariableProximity label="I believe every small moment has the potential to become an experience." className="highlight" {...commonProps} />
            <VariableProximity label=" My background in Retail and Exhibition Design taught me how to shape spaces, stories, and emotions into environments people can step into, from exhibitions and cafés to showrooms, movie sets, and museums." {...commonProps} />
          </p>
          <br />
          <p>
            <VariableProximity label="Through museum work, retail design, digital marketing, and immersive media, I’ve learned how people see, feel, move, and connect. " {...commonProps} />
            <VariableProximity label=" Each chapter has helped me build a flexible creative approach, one that blends design thinking, storytelling, spatial awareness and technology. " className="highlight" {...commonProps} />
          </p>
          <br />
          <p>
            <VariableProximity label="Now, as I pursue my Master’s in Extended Reality Technologies, I’m excited to contribute to the XR industry in any role where creativity, collaboration, and problem-solving come together. Whether it’s experience design, immersive storytelling, spatial audio, interaction design, production, research, or project coordination, " {...commonProps} />
            <VariableProximity label="I’m open to learning, adapting, and helping bring meaningful immersive experiences to life." className="highlight" {...commonProps} />
          </p>
          <br />
          <p>
            <VariableProximity label="I’m especially drawn to work where audio, space, visuals, and story resonate together, creating worlds that people don’t just see, but feel." {...commonProps} />
          </p>
          <br />
          <div className="social-icons">
            <a href="https://www.linkedin.com/in/sukhada-pande-a43512236/"><i className="fa-brands fa-linkedin"></i></a>
            <a href="mailto:smpande1@asu.edu"><i className="fa-solid fa-envelope"></i></a>
            <a
              href="/SukhadaPande_Resume_2026..pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '1.6rem', fontWeight: '600', letterSpacing: '0.1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 'auto', height: 'auto', padding: '0.6rem 1.6rem', borderRadius: '2rem' }}
            >
              Resume
            </a>
          </div>
        </div>
      </section>

      {/* frame:work section — shares LiquidEther bg from parent wrapper */}
      <div ref={frameworkDivRef} style={{ color: 'white', padding: '5rem 9%', position: 'relative', zIndex: 1 }}>
        <h3 style={{ fontSize: '2.2rem', fontWeight: '700', color: '#4fa2fa', marginBottom: '1.5rem' }}>
          frame:work New Talent List '26
        </h3>
        <p style={{ fontSize: '2.0rem', lineHeight: '1.7', marginBottom: '2rem', textAlign: 'justify' }}>
          <VariableProximity label="I was selected for the frame:work New Talent List '26 as a graduate candidate from Media Arts and Sciences at Arizona State University. The New Talent List is a curated recognition of students from international academic programs who are developing skills in video design, pixel-based entertainment technology, live entertainment, installations, XR, virtual production, and interactive experiences." {...commonProps} />
        </p>
        <p style={{ fontSize: '2.0rem', lineHeight: '1.7', marginBottom: '2rem', textAlign: 'justify' }}>
          <VariableProximity label="Being included in this list reflects my growing practice across immersive experience design, storytelling, projection systems, real-time visuals, and extended reality technologies. My profile highlights my top skills as communication, strategic problem solving, and narratives/storytelling, along with tools such as Unreal Engine, Pixera, TouchDesigner, QLab, Pro Tools, Adobe Suite, AutoCAD, and 3DS Max." {...commonProps} />
        </p>
        <p style={{ fontSize: '2.0rem', lineHeight: '1.7', marginBottom: '2rem', textAlign: 'justify' }}>
          <VariableProximity label="This recognition connects directly to the kind of work I want to continue pursuing: designing immersive environments where technology, performance, space, and story come together. It also places my work within a broader professional community of creative technologists, video designers, experiential designers, media artists, and live entertainment professionals." {...commonProps} />
        </p>
        <div style={{ marginTop: '2.5rem', textAlign: 'center' }}>
          <img
            src="/Images/image.png"
            alt="frame:work New Talent List 2026"
            style={{ maxWidth: '50%', borderRadius: '1rem', boxShadow: '0 0 20px rgba(79,162,250,0.2)' }}
          />
        </div>
      </div>
    </div>
  );
};

export default Home;